import type { FieldProps } from '~/components/shared/TextField'
import type KeyValuePair from '~/types/key-value-pair'
import { ArrowDownIcon } from '~/components/icons/ArrowDownIcon'

import React, { useState } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import { classNames } from '~/helpers/string'

export type ComboboxProps = {
  label: string
  id?: string
  name: string
  values: KeyValuePair[]
  defaultKey?: string
  onChange?: (value: string) => void
  readOnly?: boolean
  onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void
} & FieldProps

function ComboboxField(props: ComboboxProps) {
  const { error, helperText = '', disabled, readOnly, onBlur } = props
  const [selected, setSelected] = useState(props.defaultKey ?? null)
  const [query, setQuery] = useState('')

  const filteredValues =
    query === ''
      ? props.values
      : props.values.filter((item) =>
        item.value.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, '')),
      )

  function handleOnChange(value: any) {
    if (props.onChange) props.onChange(value)
    setSelected(value)
  }

  function getDisplayValue(key: string): string {
    if (!props.values) return ''

    let filtered = props.values.filter((item) => item.key === key)
    if (!filtered || !filtered.length) return ''

    return filtered[0]?.value ?? ''
  }

  return (
    <Combobox name={props.name} value={selected} onChange={handleOnChange} disabled={props.disabled}>
      {({ open }) => (
        <div className="relative min-w-32">
          <Combobox.Label
            className={classNames(
              `absolute left-3 top-2 -translate-y-4 transform bg-white px-1 text-xs duration-100`,
              error
                ? 'text-brand-red-700 focus:outline-brand-red-700'
                : 'text-brand-gray-500 peer-focus:text-brand-purple-700',
            )}
          >
            {props.label} {!props.optional && <span className="asterisk"></span>}
          </Combobox.Label>
          <div className="flex items-center">
            <Combobox.Input
              onBlur={(arg) => {
                setTimeout(() => {
                  onBlur?.(arg)
                }, 100)
              }}
              readOnly={readOnly}
              className={classNames(
                'block h-[46px] w-full rounded-md bg-transparent px-[15px] py-[13px] text-sm font-normal focus:outline-none disabled:pointer-events-none disabled:opacity-[60%]',
                'border border-brand-neutral-200 transition-all duration-100  focus:border-brand-purple-700 focus:ring-0',
                'focus-visible:border-transparent focus-visible:shadow-[0_0_0_2px_#671ddb]',
                open ? 'border-transparent shadow-[0_0_0_2px_#671ddb]' : 'hover:border-brand-neutral-500',
                error
                  ? 'border-2 border-brand-red-700 hover:border-brand-red-700 focus:border-2 focus:border-brand-red-700 '
                  : '',
              )}
              displayValue={getDisplayValue}
              onChange={(event) => {
                // Triggered by the browser autofill
                if (!query && event.target.value) {
                  const exactMatch = props.values?.find(v => v.value === event.target.value)
                  if (exactMatch) {
                    handleOnChange(exactMatch.key)
                  }
                }
                setQuery(event.target.value)
              }}
              onClick={(e: any) => {
                if (e.relatedTarget?.id?.includes('headlessui-combobox-button')) return
                !open && e.target.nextSibling.click()
              }}
            />
            <Combobox.Button className="absolute right-0 pr-3">
              <ArrowDownIcon className={`${open ? 'rotate-180' : ''} h-6 w-6`} />
            </Combobox.Button>
          </div>
          <Transition
            show={open}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            afterLeave={() => setQuery('')}
            className="z-20"
          >
            <Combobox.Options
              className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-lg bg-white py-1 text-base-content shadow-lg focus:outline-none"
              static
            >
              {filteredValues?.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none px-4 py-2 text-sm text-gray-700">
                  Nothing found.
                </div>
              ) : (
                filteredValues?.map((item) => (
                  <Combobox.Option
                    key={item.key}
                    className={({ active, selected }) =>
                      `relative cursor-default select-none py-2 pl-3 pr-4 text-base-500 ${selected ? 'bg-primary-100 text-base-content' : active ? 'bg-primary-150' : ''
                      }`
                    }
                    value={item.key}
                  >
                    <span className={`block truncate text-sm`}>{item.value}</span>
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
          {helperText && (
            <span
              className={classNames(
                'ml-4 mt-1 text-xs font-normal',
                error ? ' text-brand-red-700' : 'mt-[5px] text-brand-gray-500',
                disabled ? 'text-brand-violet-950 text-opacity-35' : '',
              )}
            >
              {helperText}
            </span>
          )}
        </div>
      )}
    </Combobox>
  )
}
export default ComboboxField
